import React, { useEffect, useState, useCallback, useRef } from 'react'
import ModernSidebarLayout from '@/components/layout/ModernSidebarLayout'
import { useAuthContext } from '@/hooks/AuthContext'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Download,
  Trash2,
  Upload,
  Video,
  Calendar,
  Clock,
  FileVideo,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Eye,
} from 'lucide-react'
import { format } from 'date-fns'

interface HighlightVideo {
  id: string
  title: string
  video_url: string
  thumbnail_url: string
  created_at: string
  status: 'processing' | 'completed' | 'failed'
  duration?: number
  size?: number
}

interface Task {
  id: string
  status: string
  created_at: string
}

const HighlightsPage = () => {
  const [highlights, setHighlights] = useState<HighlightVideo[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [deleteSuccess, setDeleteSuccess] = useState(false)
  const [processingVideo, setProcessingVideo] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadError, setUploadError] = useState('')
  const [tasks, setTasks] = useState<Task[]>([])
  const [searchTerm, setSearchTerm] = useState('')

  const apiUrl = '/api/v1/lms/highlight-videos'
  const listTasksUrl = '/api/v1/lms/highlight-generation-task/'
  const presingedUrl = '/api/v1/lms/record/generate-presigned-url/'
  const updateStatus = '/api/v1/lms/record/update-status/'
  const { state } = useAuthContext()
  const observer = useRef<IntersectionObserver | undefined>()

  const lastVideoElementRef = useCallback((node: HTMLDivElement | null) => {
    if (loading) return
    if (observer.current) observer.current.disconnect()
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setPage((prevPage) => prevPage + 1)
      }
    })
    if (node) observer.current.observe(node)
  }, [loading, hasMore])

  useEffect(() => {
    const fetchHighlights = async () => {
      if (!state?.key) return
      setLoading(true)
      try {
        const response = await fetch(`${apiUrl}?page=${page}&per_page=1000`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${state.key}`,
          },
        })
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        const data = await response.json()
        setHighlights((prevHighlights) => [...prevHighlights, ...data.results])
        setHasMore(data.page < data.total_pages)
      } catch (err) {
        console.error('Error fetching highlights:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchHighlights()
  }, [state.key, page])

  useEffect(() => {
    const fetchTasks = async () => {
      if (!state?.key) return
      try {
        const response = await fetch(listTasksUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${state.key}`,
          },
        })
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        const data = await response.json()
        setTasks(data)
      } catch (err) {
        console.error('Error fetching tasks:', err)
      }
    }

    fetchTasks()
  }, [state.key])

  const deleteHighlight = async (videoId: string) => {
    if (!state?.key) return

    try {
      const response = await fetch(`${apiUrl}/${videoId}/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${state.key}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to delete highlight')
      }

      setHighlights((prevHighlights) => prevHighlights.filter((video) => video.id !== videoId))
      setDeleteSuccess(true)
      setTimeout(() => setDeleteSuccess(false), 3000)
    } catch (err) {
      console.error('Error deleting highlight:', err)
    }
  }

  const downloadVideo = async (videoId: string, videoUrl: string) => {
    setProcessingVideo(videoId)
    try {
      await fetch(`${apiUrl}/${videoId}/track-download/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${state.key}`,
        },
      })

      const response = await fetch(videoUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `meetlala.io_${videoId}.mp4`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Error downloading video:', err)
    } finally {
      setProcessingVideo(null)
    }
  }

  const uploadFile = async (uploadEvent: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = uploadEvent.target.files?.[0]
    if (!selectedFile) return

    setUploadProgress(0)
    setUploadError('')

    try {
      const presignedData = await fetch(presingedUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${state.key}`,
        },
        body: JSON.stringify({
          file_name: selectedFile.name,
          content_type: selectedFile.type,
          size: selectedFile.size,
        }),
      }).then((res) => res.json())

      const xhr = new XMLHttpRequest()
      xhr.open('PUT', presignedData.presigned_url, true)
      xhr.setRequestHeader('Content-Type', selectedFile.type)

      xhr.upload.onprogress = (progressEvent) => {
        if (progressEvent.lengthComputable) {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100)
          setUploadProgress(progress)
        }
      }

      xhr.onload = async () => {
        if (xhr.status === 200) {
          await fetch(updateStatus, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Token ${state.key}`,
            },
            body: JSON.stringify({
              id: presignedData.id,
              status: 'uploaded',
            }),
          })

          setPage(1)
          setHighlights([])
        } else {
          setUploadError('Upload failed')
        }
      }

      xhr.send(selectedFile)
    } catch (err) {
      setUploadError('Upload failed')
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
    case 'completed':
      return <Badge variant="secondary" className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>
    case 'processing':
      return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Processing</Badge>
    case 'failed':
      return <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" />Failed</Badge>
    default:
      return <Badge variant="outline">{status}</Badge>
    }
  }

  const filteredHighlights = highlights.filter((highlight) =>
    highlight.title?.toLowerCase().includes(searchTerm.toLowerCase())
    || highlight.id.toLowerCase().includes(searchTerm.toLowerCase()))

  return (
    <ModernSidebarLayout meta={{ robots: 'noindex' }}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Video Highlights</h1>
            <p className="text-muted-foreground">Manage your video highlights and reels</p>
          </div>
          <div className="flex items-center gap-4">
            <Input
              placeholder="Search highlights..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-80"
            />
            <Button asChild>
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload className="w-4 h-4 mr-2" />
                Upload Video
              </label>
            </Button>
            <input
              id="file-upload"
              type="file"
              accept="video/*"
              onChange={uploadFile}
              className="hidden"
            />
          </div>
        </div>

        {uploadProgress > 0 && uploadProgress < 100 && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                <div className="flex-1">
                  <div className="flex justify-between text-sm mb-1">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {uploadError && (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="w-4 h-4" />
                <span>{uploadError}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {tasks.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Processing Tasks
              </CardTitle>
              <CardDescription>
                Videos currently being processed into highlights
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {tasks.map((task) => (
                  <div key={task.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Task {task.id}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(task.status)}
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(task.created_at), 'MMM d, HH:mm')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Video className="w-5 h-5" />
              Highlights Library
            </CardTitle>
            <CardDescription>
              {filteredHighlights.length} video{filteredHighlights.length !== 1 ? 's' : ''} available
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading && highlights.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                <span>Loading highlights...</span>
              </div>
            ) : filteredHighlights.length === 0 ? (
              <div className="text-center py-8">
                <FileVideo className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No highlights found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm ? 'No highlights match your search criteria.' : 'Upload your first video to get started.'}
                </p>
                {!searchTerm && (
                  <Button asChild>
                    <label htmlFor="file-upload-empty" className="cursor-pointer">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Video
                    </label>
                  </Button>
                )}
                <input
                  id="file-upload-empty"
                  type="file"
                  accept="video/*"
                  onChange={uploadFile}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Video</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredHighlights.map((highlight, index) => (
                      <TableRow
                        key={highlight.id}
                        ref={index === filteredHighlights.length - 1 ? lastVideoElementRef : null}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {highlight.thumbnail_url ? (
                              <img
                                src={highlight.thumbnail_url}
                                alt="Thumbnail"
                                className="w-16 h-12 object-cover rounded"
                              />
                            ) : (
                              <div className="w-16 h-12 bg-muted rounded flex items-center justify-center">
                                <Video className="w-6 h-6 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{highlight.title || `Video ${highlight.id}`}</div>
                            <div className="text-sm text-muted-foreground">ID: {highlight.id}</div>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(highlight.status)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="w-4 h-4" />
                            {format(new Date(highlight.created_at), 'MMM d, yyyy')}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {highlight.video_url && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(highlight.video_url, '_blank')}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                            {highlight.video_url && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => downloadVideo(highlight.id, highlight.video_url)}
                                disabled={processingVideo === highlight.id}
                              >
                                {processingVideo === highlight.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Download className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="outline" size="sm">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Delete Highlight</DialogTitle>
                                  <DialogDescription>
                                    Are you sure you want to delete this highlight? This action cannot be undone.
                                  </DialogDescription>
                                </DialogHeader>
                                <DialogFooter>
                                  <Button variant="outline">Cancel</Button>
                                  <Button
                                    variant="destructive"
                                    onClick={() => deleteHighlight(highlight.id)}
                                  >
                                    Delete
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {deleteSuccess && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span>Highlight deleted successfully</span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </ModernSidebarLayout>
  )
}

export default HighlightsPage
